import React, { useCallback } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';

import { formatCurrencyDollars } from '../../helpers/formatters';
import { useObservability } from '../../helpers/observability';

/**
 * Structural link wrapping a region of the card. Atoms `Link` is for inline
 * text links (it underlines and recolors), so container links use a layout
 * primitive with `href` instead and track clicks via `onClick` since
 * containers have no built-in click analytics.
 */
const CardLink: React.FC<{
  href?: string | null;
  ariaLabel: string;
  analyticsName: string;
  className?: string;
  children: React.ReactNode;
}> = ({ href, ariaLabel, analyticsName, className, children }) => {
  const { trackJourneyEvent } = useObservability();

  const handleClick = useCallback(() => {
    trackJourneyEvent('platform', 'linkClickEvent', { targetName: analyticsName });
  }, [trackJourneyEvent, analyticsName]);

  if (href == null) {
    return <Col className={className}>{children}</Col>;
  }

  return (
    <Col href={href} aria-label={ariaLabel} onClick={handleClick} className={className}>
      {children}
    </Col>
  );
};

export type PropertyCardProps = {
  beds?: number | null;
  baths?: number | null;
  city?: string | null;
  dwellingType?: string | null;
  href?: string | null;
  isFavorite?: boolean | null;
  isFavoriteLoading?: boolean;
  neighborhood?: string | null;
  onFavoritePress?: () => void;
  isPhotoPending?: boolean;
  photoUrl?: string | null;
  /**
   * Rendered inside the photo container, on top of the photo — badges, hover
   * treatments, etc. Position children with `absolute`; the container is
   * `relative` and clips to the photo's rounded bounds, so consumers never
   * have to mirror the card's aspect ratio or radius.
   */
  photoOverlay?: React.ReactNode;
  photoFetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  price?: number | null;
  /** Shows a "Self-tour available" badge next to the price. */
  selfTourAvailable?: boolean | null;
  sqft?: number | null;
};

const PROPERTY_CARD_IMAGE_CLASS = 'aspect-[16/9] w-full rounded-2xl object-cover';

export const PropertyCard: React.FC<PropertyCardProps> = ({
  beds,
  baths,
  city,
  dwellingType,
  href,
  isFavorite,
  isFavoriteLoading,
  neighborhood,
  onFavoritePress,
  isPhotoPending,
  photoUrl,
  photoFetchPriority,
  photoOverlay,
  price,
  selfTourAvailable,
  sqft,
}) => {
  return (
    <Col className="w-full gap-5">
      <CardLink
        href={href}
        ariaLabel="Main view of property"
        analyticsName="marketplace-property-card-image-link"
        className="relative w-full overflow-hidden rounded-2xl"
      >
        <PropertyPhotoImage
          alt="Main view of property"
          loading="lazy"
          containerClassName={PROPERTY_CARD_IMAGE_CLASS}
          className="h-full w-full object-cover"
          placeholderClassName="bg-od-bg-tertiary"
          isPending={isPhotoPending}
          src={photoUrl}
          fetchPriority={photoFetchPriority}
        />
        {photoOverlay}
      </CardLink>
      <Col className="gap-3">
        <Row className="items-center justify-between">
          <Row className="items-center gap-2">
            {price != null && (
              <Text as="span" className="text-subheader-sm text-od-content-primary">
                {formatCurrencyDollars(price)}
              </Text>
            )}
            {selfTourAvailable && (
              <Box className="rounded-full bg-od-bg-accent-brand px-2 py-1">
                <Text as="span" className="text-label-xs text-od-content-inverse-primary">
                  Self-tour
                </Text>
              </Box>
            )}
          </Row>
          {onFavoritePress && (
            <Row className="justify-end self-center">
              <Button
                variant="secondary"
                aria-label={isFavorite ? 'unfavorite' : 'favorite'}
                analyticsName="marketplace-property-card-favorite-button"
                onClick={onFavoritePress}
                loading={isFavoriteLoading}
                className="size-6 rounded-full border-0 bg-transparent p-0"
              >
                {isFavorite ? (
                  <IconHeartFilled size={20} className="text-od-content-error" />
                ) : (
                  <IconHeart size={20} />
                )}
              </Button>
            </Row>
          )}
        </Row>
        <CardLink
          href={href}
          ariaLabel="Property details"
          analyticsName="marketplace-property-card-info-link"
          className="w-full"
        >
          <Row className="w-full items-baseline whitespace-nowrap">
            <Text as="span" className="text-body-sm text-od-content-tertiary">
              {beds != null && `${beds} bds`}
            </Text>
            {baths != null && (
              <>
                <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                  {' • '}
                </Text>
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {baths} ba
                </Text>
              </>
            )}
            {sqft != null && (
              <>
                <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                  {' • '}
                </Text>
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {sqft.toLocaleString('en-US')} sqft
                </Text>
              </>
            )}
            {dwellingType && (
              <>
                <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                  {' • '}
                </Text>
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {dwellingType}
                </Text>
              </>
            )}
          </Row>
          <Row className="items-baseline gap-0.5">
            {neighborhood && (
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {city ? `${neighborhood}, ` : neighborhood}
              </Text>
            )}
            {city && (
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {city}
              </Text>
            )}
          </Row>
        </CardLink>
      </Col>
    </Col>
  );
};
